import {ISoundsPlayer} from '@src/@interfaces/services/SoundsPlayer/ISoundsPlayer';
import {ISound} from '@src/@interfaces/services/SoundsPlayer/ISound';
import config from '@src/helpers/config';

export default class Sound implements ISound {
    protected audio: HTMLAudioElement | null = null;

    protected volume!: number;

    constructor(
        protected player: ISoundsPlayer,
        protected soundName: string,
        protected soundSrc: string,
    ) {
        const configKey = `settings.notifications.${soundName}.sound.volume` as any;

        this.volume = config(configKey, 1);
        config().onValueChanged(configKey, (e, payload) => {
            this.volume = payload.value;
        });
    }

    getName(): string {
        return this.soundName;
    }

    preload(): Promise<void> {
        return new Promise<void>((res, rej) => {
            if (this.isLoaded()) {
                return void res();
            }

            this.audio = new Audio(this.soundSrc);
            const readyListener = () => {
                if (this.audio?.readyState === 4) {
                    res();
                }
            };

            this.audio.addEventListener('canplay', readyListener);
            this.audio.addEventListener('canplaythrough', readyListener);
            this.audio.addEventListener('error', e => rej(e));
        });
    }

    isLoaded(): boolean {
        return this.audio != null;
    }

    async pause(): Promise<void> {
        await this.load().then(audio => audio.pause());
    }

    async play(): Promise<void> {
        await this.stop();

        return this.load().then(audio => {
            audio.volume = this.volume;
            audio.play().catch();
        });
    }

    async stop(): Promise<void> {
        await this.pause();
        await this.seek(0);
    }

    async seek(time: number): Promise<void> {
        await this.load().then(audio => {
            audio.currentTime = time;
        });
    }

    on<K extends keyof HTMLMediaElementEventMap>(
        event: K,
        listener: (this: HTMLAudioElement, event: HTMLMediaElementEventMap[K]) => any
    ): { unsubscribe: () => void } {
        let ref: HTMLAudioElement | undefined;
        this.load().then(audio => (ref = audio).addEventListener(event, listener));

        return {
            unsubscribe() {
                ref?.removeEventListener(event, listener);
                ref = undefined;
            }
        };
    }

    once<K extends keyof HTMLMediaElementEventMap>(
        event: K,
        listener: (this: HTMLAudioElement, event: HTMLMediaElementEventMap[K]) => any
    ): { unsubscribe: () => void } {
        let subscription: ReturnType<typeof this.on> | null = null;
        const wrappedListener = function (
            this: ThisParameterType<typeof listener>,
            ...args: Parameters<typeof listener>
        ) {
            subscription?.unsubscribe();
            listener.apply(this, args);
        };

        return subscription = this.on(event, wrappedListener);
    }

    protected async load() {
        await this.preload();

        return this.audio as HTMLAudioElement;
    }
}
